import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CheckCircle,
  Close,
  Add,
  Remove
} from '@mui/icons-material';
import orderService from '../services/orderService';

const ApproveOrderDialog = ({ open, onClose, order, onSuccess }) => {
  const [items, setItems] = useState(
    order?.items?.map(item => ({
      ...item,
      newQuantity: item.quantity
    })) || []
  );
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [adminNotes, setAdminNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleQuantityChange = (index, newQuantity) => {
    if (newQuantity < 0) return;

    const updatedItems = [...items];
    updatedItems[index].newQuantity = newQuantity;
    setItems(updatedItems);
  };

  const handleApprove = async () => {
    try {
      setLoading(true);
      setError('');

      // Verificar que todas las cantidades sean válidas
      const hasInvalidQuantity = items.some(item => item.newQuantity <= 0);
      if (hasInvalidQuantity) {
        setError('Todas las cantidades deben ser mayores a 0');
        setLoading(false);
        return;
      }

      // Preparar datos
      const modifiedItems = items
        .filter(item => item.newQuantity !== item.quantity)
        .map(item => ({
          productId: item.productId,
          quantity: item.newQuantity
        }));

      const approvalData = {
        paymentMethod,
        adminNotes: adminNotes.trim() || undefined,
        items: modifiedItems.length > 0 ? modifiedItems : undefined
      };

      // Enviar aprobación
      const result = await orderService.approve(order.id, approvalData);

      if (result.success) {
        onSuccess(result.data);
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error al aprobar pedido:', err);
      setError('Error al aprobar el pedido');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(price || 0);
  };

  const calculateNewTotal = () => {
    return items.reduce((sum, item) => {
      return sum + (item.unitPrice * item.newQuantity);
    }, 0);
  };

  const hasModifications = items.some(item => item.newQuantity !== item.quantity);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle color="success" />
          <Typography variant="h6">
            Aprobar Pedido #{order?.orderNumber}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 3 }}>
          Al aprobar este pedido, se creará automáticamente una venta y se descontará el stock.
          Puedes modificar las cantidades si es necesario antes de aprobar.
        </Alert>

        {/* Tabla de Items */}
        <Typography variant="subtitle2" gutterBottom>
          Productos del Pedido:
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Producto</strong></TableCell>
                <TableCell align="center"><strong>Cantidad Original</strong></TableCell>
                <TableCell align="center"><strong>Nueva Cantidad</strong></TableCell>
                <TableCell align="right"><strong>Precio Unit.</strong></TableCell>
                <TableCell align="right"><strong>Subtotal</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow
                  key={item.id}
                  sx={{
                    bgcolor: item.newQuantity !== item.quantity ? 'warning.lighter' : 'inherit'
                  }}
                >
                  <TableCell>
                    <Typography variant="body2">
                      {item.productName}
                    </Typography>
                    {item.productCode && (
                      <Typography variant="caption" color="text.secondary">
                        Código: {item.productCode}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {item.quantity}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleQuantityChange(index, item.newQuantity - 1)}
                        disabled={item.newQuantity <= 1 || loading}
                      >
                        <Remove fontSize="small" />
                      </IconButton>
                      <TextField
                        size="small"
                        value={item.newQuantity}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 0;
                          handleQuantityChange(index, value);
                        }}
                        inputProps={{
                          min: 0,
                          style: { textAlign: 'center', width: '60px' }
                        }}
                        type="number"
                        disabled={loading}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleQuantityChange(index, item.newQuantity + 1)}
                        disabled={loading}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatPrice(item.unitPrice)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: item.newQuantity !== item.quantity ? 600 : 400 }}>
                      {formatPrice(item.unitPrice * item.newQuantity)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Total */}
        <Box sx={{ p: 2, bgcolor: hasModifications ? 'warning.lighter' : 'success.lighter', borderRadius: 1, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Total {hasModifications && '(Modificado)'}:
            </Typography>
            <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
              {formatPrice(calculateNewTotal())}
            </Typography>
          </Box>
          {hasModifications && (
            <Typography variant="caption" color="text.secondary">
              Total original: {formatPrice(order?.total)}
            </Typography>
          )}
        </Box>

        {/* Método de Pago */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Método de Pago</InputLabel>
          <Select
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            label="Método de Pago"
            disabled={loading}
          >
            <MenuItem value="efectivo">Efectivo</MenuItem>
            <MenuItem value="tarjeta">Tarjeta</MenuItem>
            <MenuItem value="transferencia">Transferencia</MenuItem>
            <MenuItem value="mercadopago">Mercado Pago</MenuItem>
            <MenuItem value="otro">Otro</MenuItem>
          </Select>
        </FormControl>

        {/* Notas Internas */}
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Notas Internas (opcional)"
          placeholder="Ej: Cliente pidió factura A, entregar el lunes..."
          value={adminNotes}
          onChange={(e) => setAdminNotes(e.target.value)}
          disabled={loading}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          startIcon={<Close />}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleApprove}
          variant="contained"
          color="success"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
        >
          {loading ? 'Aprobando...' : 'Aprobar y Crear Venta'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApproveOrderDialog;

